import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import _ from 'lodash';
import { PromoEventKPIs } from 'types/tradePromoOptimization';
interface FetchPromoKpisConfigProp extends HookInputProps {
    setPromoEventKPIs: Dispatch<SetStateAction<PromoEventKPIs | null>>;
}
function useFetchPromoKPIsConfigData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    setPromoEventKPIs,
}: FetchPromoKpisConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });
    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);
    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && Array.isArray(payload)) {
                //setting all required states
                setPromoEventKPIs({
                    metricConfig: [
                        {
                            name: 'Sales Adjusted Base',
                            value: 'sales_adjusted_base',
                            isSelected: true,
                            displayOrder: 1,
                        },
                        {
                            name: 'Overlapping Lift',
                            value: 'overlapping_lift',
                            isSelected: true,
                            displayOrder: 2,
                        },
                        {
                            name: 'EDLP SRP',
                            value: 'edlp_srp',
                            isSelected: true,
                            displayOrder: 3,
                        },
                        {
                            name: 'Promo SRP',
                            value: 'promo_srp',
                            isSelected: false,
                            displayOrder: 4,
                        },
                        {
                            name: 'Uplift Volume',
                            value: 'uplift_volume',
                            isSelected: false,
                            displayOrder: 5,
                        },
                        {
                            name: 'Smooth Base Volume',
                            value: 'smooth_base_volume',
                            isSelected: false,
                            displayOrder: 6,
                        },
                    ],
                    spendConfig: [
                        {
                            name: 'OI Rate',
                            value: 'oi_rate',
                            isSelected: true,
                            displayOrder: 1,
                        },
                        {
                            name: 'NBOI Percent',
                            value: 'nboi_percent',
                            isSelected: true,
                            displayOrder: 2,
                        },
                        {
                            name: 'NBOI Rate',
                            value: 'nboi_rate',
                            isSelected: false,
                            displayOrder: 3,
                        },
                        {
                            name: 'Bill Back Percent',
                            value: 'bill_back_percent',
                            isSelected: false,
                            displayOrder: 4,
                        },
                        {
                            name: 'Bill Back Rate',
                            value: 'bill_back_rate',
                            isSelected: false,
                            displayOrder: 5,
                        },
                        {
                            name: 'Scan Rate',
                            value: 'scan_rate',
                            isSelected: false,
                            displayOrder: 6,
                        },
                    ],
                });
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}
export { useFetchPromoKPIsConfigData };
